import { BaseText, Headline3, Meta, Spinner, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import IconIndicator from 'components/icons/IconIndicator';
import { isFunction } from 'lodash';
import React from 'react';
import { AccessibilityRole, StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';
import { Divider, Pill } from 'styles/CommonLayoutStyles';
import { MenuRowWrapper, MenuWrapper } from 'styles/ContainerStyles';
import { PmcVisibility } from 'types/generatedSchemaTypes';
import { IndicatorType } from 'types/IconTypes';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

const StyledView = styled.View<{
  mt?: number;
}>`
  margin-right: 15px;
  margin-top: ${({ mt }) => mt ?? 0}px;
`;

const IconWrapper = styled.View`
  padding-right: 5px;
`;

const EndAlignedIconView = styled.View<{
  pl?: number;
  pr?: number;
}>`
  justify-content: center;
  align-items: flex-end;
  flex-shrink: 0;
  align-self: center;
  padding-left: ${({ pl }) => pl ?? 0}px;
  padding-right: ${({ pr }) => pr ?? 0}px;
`;

const RightComponentWrapper = styled.View<{
  mr?: number;
}>`
  justify-content: center;
  align-items: flex-end;
  flex-shrink: 0;
  align-self: center;
  max-width: ${!isWeb ? theme.normalising.widthPixel(250) : '600'}px;
  margin-right: ${({ mr }) => mr ?? 0}px;
`;

export const TextWrapper = styled.View`
  flex: 1;
  min-width: 0;
`;

const SmallLabel = styled(Meta)`
  max-width: 150px;
  overflow: hidden;
`;

export enum WarningType {
  ERROR = 'error',
  WARNING = 'warning',
}

export type MenuItemProps = {
  iconName?: string;
  iconFlexStart?: boolean;
  iconMt?: number;
  indicatorType?: IndicatorType;
  indicatorNumber?: number;
  menuLabel: string;
  color?: string;
  testID?: string;
  noBorder?: boolean;
  onPress?: () => void;
  loading?: boolean;
  large?: boolean;
  background?: string;
  withArrow?: boolean;
  disabled?: boolean;
  arrowSize?: string;
  borderW?: number;
  subtitle?: string | React.ReactNode;
  pillText?: string;
  smallLabel?: string;
  smallLabelColor?: string;
  paddingVertical?: number;
  borderColor?: string;
  bold?: boolean;
  rightComponent?: React.ReactElement | false;
  imageComponent?: React.ReactElement | false;
  bottomPillText?: string;
  arrowIcon?: string;
  divider?: boolean;
  borderTopWidth?: number;
  warning?: { type: WarningType; amount: number };
  visibility?: PmcVisibility;
  unlocked?: boolean;
  accessibilityRole?: AccessibilityRole;
  importantForAccessibility?: 'auto' | 'yes' | 'no' | 'no-hide-descendants';
  containsInteractiveElement?: boolean;
  accessible?: boolean;
  // When set, overrides the default menuLabel / subtitle-derived label.
  accessibilityLabel?: string;
  pillConfig?: PillConfig;
};

type PillConfig = {
  bg?: string;
  alignLeft?: boolean;
  mt?: number;
  mb?: number;
  mr?: number;
  ml?: number;
  px?: number;
  py?: number;
};

type MenuLabelProps = {
  bold: boolean;
  color?: string;
  testID?: string;
  menuLabel: string;
  loading: boolean;
};

const MenuLabel: React.FC<MenuLabelProps> = ({
  bold,
  color,
  testID,
  menuLabel,
  loading,
}) => {
  if (bold) {
    return (
      <Headline3
        color={color}
        {...testIdentifiers(`${testID as string}-text`)}
        numberOfLines={1}>
        {menuLabel}
      </Headline3>
    );
  }
  return (
    <BaseText
      color={color}
      testID={testID ? testID + '-text' : undefined}
      numberOfLines={1}>
      {menuLabel}
      {loading ? ' ' : ''}
      {loading && <Spinner small />}
    </BaseText>
  );
};

const MenuItem: React.FC<MenuItemProps> = ({
  iconName,
  iconFlexStart,
  iconMt,
  indicatorType,
  indicatorNumber,
  menuLabel,
  color,
  testID,
  background,
  pillText = '',
  smallLabel = '',
  smallLabelColor,
  noBorder = false,
  subtitle,
  onPress,
  large = false,
  loading = false,
  withArrow = false,
  arrowSize,
  borderW = 3,
  disabled = false,
  borderColor = theme.colors.gray50,
  bold = false,
  paddingVertical = 15,
  rightComponent,
  imageComponent,
  bottomPillText,
  arrowIcon = 'Chevron_Right',
  divider = false,
  borderTopWidth,
  warning = undefined,
  visibility,
  unlocked,
  accessibilityRole = 'button' as AccessibilityRole,
  importantForAccessibility = 'yes',
  containsInteractiveElement = false,
  accessible,
  accessibilityLabel: accessibilityLabelProp,
  pillConfig = { mr: 3, ml: 3 },
}) => {
  const borderWidth = () => {
    if (noBorder) {
      return 0;
    }
    return borderW;
  };

  return (
    <MenuWrapper
      {...testIdentifiers(testID as string)}
      borderWidth={borderWidth()}
      borderColor={borderColor}
      background={background}
      borderTopWidth={borderTopWidth}
      paddingVertical={paddingVertical}
      onPress={onPress}
      disabled={disabled || !isFunction(onPress)}
      style={({ pressed }: { pressed: boolean }) => [
        { opacity: pressed || loading || disabled ? 0.5 : 1 },
      ]}
      accessible={accessible ?? !containsInteractiveElement}
      accessibilityRole={
        !containsInteractiveElement ? accessibilityRole : undefined
      }
      accessibilityLabel={
        accessibilityLabelProp ??
        (!containsInteractiveElement &&
        subtitle &&
        typeof subtitle === 'string'
          ? `${menuLabel}. ${subtitle}`
          : menuLabel)
      }
      importantForAccessibility={
        containsInteractiveElement
          ? 'no-hide-descendants'
          : importantForAccessibility
      }>
      {divider && <Divider down={true} size={3} color={'#cdd0d1'} />}
      <MenuRowWrapper flexStart={iconFlexStart}>
        {!!iconName && (
          <StyledView mt={iconMt}>
            <Icon
              name={iconName}
              color={color}
              size={large ? 'l' : 'm'}
              indicatorType={indicatorType}
              indicatorNumber={indicatorNumber}
              testID={testID ? `${testID}-icon` : undefined}
            />
            {warning && warning.amount > 0 && (
              <View style={styles.warningNumber}>
                <IconIndicator
                  fullSize
                  indicatorType={warning.type}
                  indicatorNumber={warning.amount}
                />
              </View>
            )}
          </StyledView>
        )}
        {imageComponent && <StyledView>{imageComponent}</StyledView>}
        <TextWrapper>
          {menuLabel && (
            <MenuLabel
              bold={bold}
              color={color}
              testID={testID}
              menuLabel={menuLabel}
              loading={loading}
            />
          )}
          {subtitle && (
            <Meta
              {...testIdentifiers(testID + '-subtext')}
              color={theme.colors.gray300}>
              {subtitle}
            </Meta>
          )}
        </TextWrapper>
        {pillText?.length > 0 && (
          <Pill
            {...pillConfig}
            testID={testID ? testID + '-pill-text' : undefined}>
            {visibility === 'LOCKED' && (
              <IconWrapper>
                <Icon
                  name="Locked-Filled"
                  color={theme.colors.black}
                  size="s"
                />
              </IconWrapper>
            )}
            <Meta>{pillText}</Meta>
          </Pill>
        )}
        {smallLabel?.length > 0 && (
          <SmallLabel
            numberOfLines={1}
            ellipsizeMode="tail"
            color={smallLabelColor ?? theme.colors.gray300}
            testID={testID ? testID + '-small-label' : undefined}>
            {smallLabel}
          </SmallLabel>
        )}
        {rightComponent && (
          <RightComponentWrapper
            accessible={false}
            {...testIdentifiers(testID + '-right-component')}
            mr={withArrow ? 15 : 0}>
            {rightComponent}
          </RightComponentWrapper>
        )}
        {withArrow && (
          <EndAlignedIconView>
            <Icon
              name={arrowIcon}
              color={smallLabel?.length > 0 ? smallLabelColor : color}
              size={arrowSize}
            />
          </EndAlignedIconView>
        )}
      </MenuRowWrapper>
      {divider && <Divider up={true} size={3} color={'#cdd0d1'} />}
      {bottomPillText && (
        <Pill
          mt={10}
          testID={testID ? testID + '-bottom-pill-text' : undefined}>
          <Meta>{bottomPillText}</Meta>
        </Pill>
      )}
    </MenuWrapper>
  );
};

const styles = StyleSheet.create({
  warningNumber: {
    position: 'absolute',
    top: -4,
    right: -4,
  },
  pressable: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
});

export default MenuItem;
